import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Button,
  cn,
  DateRangeField,
  Label,
  NumberInput,
  TimeInput,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@tellescope/ui";
import { ChevronRightIcon, Settings2, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IntervalSelector } from "./IntervalSelector";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useCallback, useMemo } from "react";
import { createIdMap } from "@tellescope/utils";
import { UniqueWeeklyAvailability } from "./AvailabilityView";
import { MonthlyRestriction } from "@tellescope/types-models";

const minutesToTimeString = (total: number) =>
  `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;

const timeStringToMinutes = (timeString: string): number => {
  const [hourStr, minuteStr] = timeString.split(":");
  return (Number(hourStr) || 0) * 60 + (Number(minuteStr) || 0);
};

export interface AvailabilityBlockProps {
  block: UniqueWeeklyAvailability;
  onChange: (block: UniqueWeeklyAvailability) => void;
  onDelete: () => void;
  canUpdate: boolean;
}

export const AvailabilityBlock = ({ block, onChange, onDelete, canUpdate }: AvailabilityBlockProps) => {
  const { t } = useTranslation("calendar");
  const allTemplatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });
  const allTemplatesMap = useMemo(() => createIdMap(allTemplatesQuery.data ?? []), [allTemplatesQuery.data]);
  const selectedTemplates = useMemo(() => {
    if (block.validTemplateIds?.length) {
      return block.validTemplateIds.reduce<TellescopeModelFromName<"calendar_event_templates">[]>((acc, id) => {
        const match = allTemplatesMap[id];
        if (match) {
          acc.push(match);
        }
        return acc;
      }, []);
    }
    return allTemplatesQuery.data ?? [];
  }, [allTemplatesMap, allTemplatesQuery.data, block.validTemplateIds]);
  const selectedTemplateIdsSet = useMemo(() => new Set(block.validTemplateIds ?? []), [block.validTemplateIds]);

  const toggleEvent = useCallback(
    (id: string) => {
      const isSelected = selectedTemplateIdsSet.has(id);
      const selectedClone = new Set(selectedTemplateIdsSet);
      if (isSelected) {
        selectedClone.delete(id);
      } else {
        selectedClone.add(id);
      }
      onChange({
        ...block,
        validTemplateIds: Array.from(selectedClone),
      });
    },
    [block, onChange, selectedTemplateIdsSet]
  );

  const toggleMonthlyRestriction = useCallback(
    (value: MonthlyRestriction["occurrences"][number]) => {
      const isSelected = block.monthlyRestriction?.occurrences.includes(value);
      if (isSelected) {
        onChange({
          ...block,
          monthlyRestriction: {
            ...block.monthlyRestriction,
            occurrences: block.monthlyRestriction?.occurrences.filter((val) => val !== value) ?? [],
          },
        });
      } else {
        onChange({
          ...block,
          monthlyRestriction: {
            ...block.monthlyRestriction,
            occurrences: [...(block.monthlyRestriction?.occurrences ?? []), value],
          },
        });
      }
    },
    [block, onChange]
  );

  return (
    <AccordionItem
      value="block"
      data-testid={`availability-block-${block._originalIndex}`}
      className="rounded-sm border last:border-b"
    >
      <AccordionHeader className="flex items-center gap-2 justify-between p-2">
        <div className="flex items-center gap-1">
          <TimeInput
            value={minutesToTimeString(block.startTimeInMinutes)}
            onChange={(value) =>
              onChange({
                ...block,
                startTimeInMinutes: timeStringToMinutes(value),
              })
            }
            className="w-[100px]"
            inputProps={{
              className: "font-light md:text-xs text-xs pr-[30px]",
            }}
            disabled={!canUpdate}
          />
          <span>-</span>
          <TimeInput
            value={minutesToTimeString(block.endTimeInMinutes)}
            onChange={(value) =>
              onChange({
                ...block,
                endTimeInMinutes: timeStringToMinutes(value),
              })
            }
            className="w-[100px]"
            inputProps={{
              className: "font-light md:text-xs text-xs pr-[30px]",
            }}
            disabled={!canUpdate}
          />
          <span className="flex gap-0 items-center">
            {selectedTemplates.slice(0, 3).map((template) => (
              <Button
                key={template?.id}
                size="sm"
                variant="ghost"
                className="text-muted-foreground font-light text-xs"
                onClick={() =>
                  onChange({
                    ...block,
                    validTemplateIds:
                      block.validTemplateIds?.filter((id) => id !== template.id) ??
                      Object.keys(allTemplatesMap).filter((id) => id !== template.id),
                  })
                }
                disabled={!canUpdate}
              >
                {template.displayTitle || template.title}
              </Button>
            ))}
            {selectedTemplates.length > 3 && (
              <AccordionTrigger withoutHeader chevronPosition="none" className="py-0 hover:no-underline">
                <Button variant="ghost" size="sm-icon" className="h-auto py-1 text-muted-foreground font-light text-xs">
                  {t("+{{amount}}", { amount: selectedTemplates.length - 3 })}
                </Button>
              </AccordionTrigger>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger>
              <span className="flex items-center gap-1">
                <Label htmlFor="priority" className="text-muted-foreground text-xs">
                  {t("Priority").slice(0, 1)}
                </Label>
                <NumberInput
                  value={block.priority}
                  step={1}
                  max={10}
                  min={0}
                  className="text-xs pl-2 pr-1 gap-0 h-[25px] md:text-xs w-[50px]"
                  onChange={(priority) =>
                    onChange({
                      ...block,
                      priority: priority === 0 ? undefined : priority,
                    })
                  }
                  placeholder={t("N/A")}
                  disabled={!canUpdate}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>{t("Priority (1–10): higher slots shown first to patients")}</TooltipContent>
          </Tooltip>
          <AccordionTrigger withoutHeader chevronPosition="none" className="py-0 group">
            <Button variant="ghost" size="sm" className="text-muted-foreground px-1 py-1.5 h-auto">
              <Settings2 className="size-3.5" />
              <ChevronRightIcon className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
            </Button>
          </AccordionTrigger>
          <Button size="sm-icon" variant="ghost" onClick={onDelete} disabled={!canUpdate}>
            <Trash2 />
          </Button>
        </div>
      </AccordionHeader>
      <AccordionContent className="border-t p-3 pt-4 bg-gray-50 rounded-b-sm space-y-4">
        <div className="flex gap-2 items-center w-full">
          <IntervalSelector
            label={t("Slot interval (min)")}
            className="flex-1"
            value={block.intervalInMinutes}
            onChange={(intervalInMinutes) => onChange({ ...block, intervalInMinutes })}
            disabled={!canUpdate}
          />
          <IntervalSelector
            label={t("Pre-buffer (min)")}
            defaultValue={t("None")}
            className="flex-1"
            value={block.bufferStartMinutes}
            onChange={(bufferStartMinutes) => onChange({ ...block, bufferStartMinutes })}
            intervals={[5, 10, 15, 20, 30]}
            disabled={!canUpdate}
          />
          <div className="space-y-1 flex-[2]">
            <Label className="text-muted-foreground text-xs font-light">{t("Active range")}</Label>
            <DateRangeField
              value={{
                from: typeof block.active?.from === "object" ? block.active.from.getTime() : undefined,
                to: typeof block.active?.to === "object" ? block.active.to.getTime() : undefined,
              }}
              onChange={({ from, to }) => {
                onChange({
                  ...block,
                  active: { from: from ? new Date(from) : undefined, to: to ? new Date(to) : undefined },
                });
              }}
              className="text-xs h-auto py-1 grow"
              disabled={!canUpdate}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs font-light">{t("Event types (blank = all)")}</Label>
          <div className="flex flex-wrap gap-1 text-xs font-light">
            {allTemplatesQuery.data?.map((template) => {
              const isSelected = selectedTemplateIdsSet.has(template.id);
              return (
                <Button
                  key={`${template.id}-selector`}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  className={cn("text-xs font-light h-auto py-1", isSelected && "border-primary border")}
                  onClick={() => toggleEvent(template.id)}
                  disabled={!canUpdate}
                >
                  {template.displayTitle || template.title}
                </Button>
              );
            }) ?? t("No event types to display")}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs font-light">
            {t("Monthly restriction (blank = every week)")}
          </Label>
          <div className="flex flex-wrap gap-1 text-xs font-light">
            <Button
              variant={block.monthlyRestriction?.occurrences.includes(1) ? "default" : "outline"}
              size="sm"
              className={cn(
                "text-xs font-light h-auto py-1",
                block.monthlyRestriction?.occurrences.includes(1) && "border-primary border"
              )}
              onClick={() => toggleMonthlyRestriction(1)}
              disabled={!canUpdate}
            >
              {t("1st")}
            </Button>
            <Button
              variant={block.monthlyRestriction?.occurrences.includes(2) ? "default" : "outline"}
              size="sm"
              className={cn(
                "text-xs font-light h-auto py-1",
                block.monthlyRestriction?.occurrences.includes(2) && "border-primary border"
              )}
              onClick={() => toggleMonthlyRestriction(2)}
              disabled={!canUpdate}
            >
              {t("2nd")}
            </Button>
            <Button
              variant={block.monthlyRestriction?.occurrences.includes(3) ? "default" : "outline"}
              size="sm"
              className={cn(
                "text-xs font-light h-auto py-1",
                block.monthlyRestriction?.occurrences.includes(3) && "border-primary border"
              )}
              onClick={() => toggleMonthlyRestriction(3)}
              disabled={!canUpdate}
            >
              {t("3rd")}
            </Button>
            <Button
              variant={block.monthlyRestriction?.occurrences.includes(4) ? "default" : "outline"}
              size="sm"
              className={cn(
                "text-xs font-light h-auto py-1",
                block.monthlyRestriction?.occurrences.includes(4) && "border-primary border"
              )}
              onClick={() => toggleMonthlyRestriction(4)}
              disabled={!canUpdate}
            >
              {t("4th")}
            </Button>
            <Button
              variant={block.monthlyRestriction?.occurrences.includes(5) ? "default" : "outline"}
              size="sm"
              className={cn(
                "text-xs font-light h-auto py-1",
                block.monthlyRestriction?.occurrences.includes(5) && "border-primary border"
              )}
              onClick={() => toggleMonthlyRestriction(5)}
              disabled={!canUpdate}
            >
              {t("5th")}
            </Button>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
